import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { buildPayload, evaluateSurvey, type Identity } from '@webforge-survey/shared';

import {
  getSurveyDefinition,
  createSession,
  getSession,
  submitSession,
  optInIdentity,
  getErrorMessage,
} from '@/lib/api';
import {
  readResumeToken,
  storeResumeToken,
  clearResumeToken,
} from '@/lib/resume-token';
import { useRespondentStore } from './answers-store';
import { ConsentScreen } from './ConsentScreen';
import { RespondentRenderer } from './RespondentRenderer';
import { ProgressBar } from './ProgressBar';
import { Autosave, AutosaveStatusBadge } from './Autosave';
import { HelplinePage } from './HelplinePage';
import { IdentityScreen } from './IdentityScreen';

/**
 * Application répondant — point d'entrée.
 *
 * Cycle :
 * 1. Charge la définition (slug).
 * 2. Si resumeToken local → tente getSession → hydrate les réponses.
 *    Sinon affiche ConsentScreen.
 * 3. À l'acceptation → createSession + stockage du token opaque local.
 * 4. Rendu bloc-par-bloc via RespondentRenderer.
 * 5. À la fin :
 *    - mode anonymous → submit direct.
 *    - mode optional → IdentityScreen post-questions.
 *    - mode required → IdentityScreen pré-questions (donc déjà passé).
 * 6. Page de fin avec helpline + confirmation.
 */
export default function RespondentApp() {
  const { slug } = useParams<{ slug: string }>();

  if (!slug) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-bg">
        <p className="text-text-muted">Lien invalide.</p>
      </main>
    );
  }

  return <RespondentAppInner slug={slug} />;
}

interface InnerProps {
  slug: string;
}

type Stage =
  | { kind: 'consent' }
  | { kind: 'identity_required' }
  | { kind: 'survey'; blockIndex: number }
  | { kind: 'identity_optional' }
  | { kind: 'submitting' }
  | { kind: 'done' };

function RespondentAppInner({ slug }: InnerProps) {
  // Sélecteurs ciblés — évite les re-renders globaux.
  const setDefinition = useRespondentStore((s) => s.setDefinition);
  const setSessionId = useRespondentStore((s) => s.setSessionId);
  const setResumeToken = useRespondentStore((s) => s.setResumeToken);
  const hydrate = useRespondentStore((s) => s.hydrate);
  const reset = useRespondentStore((s) => s.reset);
  const setIdentity = useRespondentStore((s) => s.setIdentity);
  const setIdentityOptIn = useRespondentStore((s) => s.setIdentityOptIn);
  const sessionId = useRespondentStore((s) => s.sessionId);
  const resumeToken = useRespondentStore((s) => s.resumeToken);
  const identity = useRespondentStore((s) => s.identity);

  const [stage, setStage] = useState<Stage>({ kind: 'consent' });
  const [submitError, setSubmitError] = useState<string | null>(null);

  // 1. Charger la définition
  const defQuery = useQuery({
    queryKey: ['survey-def', slug],
    queryFn: () => getSurveyDefinition(slug),
    staleTime: Infinity,
  });

  // 2. Pousser la définition dans le store dès qu'elle est chargée
  useEffect(() => {
    if (defQuery.data) setDefinition(defQuery.data);
  }, [defQuery.data, setDefinition]);

  // 3. Tenter une reprise via token local
  useEffect(() => {
    if (!defQuery.data) return;
    if (sessionId) return; // déjà en session

    const def = defQuery.data;
    const token = readResumeToken(def.id);
    if (!token) return;

    let cancelled = false;
    void getSession(slug, token)
      .then((sess) => {
        if (cancelled || !sess) return;
        if (sess.status !== 'in_progress') {
          clearResumeToken(def.id);
          return;
        }
        setSessionId(sess.sessionId);
        setResumeToken(token);
        hydrate(sess.answers, sess.identity ?? null);
        if (def.identityMode === 'required') {
          if (!sess.identity || Object.keys(sess.identity).length === 0) {
            setStage({ kind: 'identity_required' });
          } else {
            setStage({ kind: 'survey', blockIndex: 0 });
          }
        } else {
          setStage({ kind: 'survey', blockIndex: 0 });
        }
      })
      .catch(() => {
        clearResumeToken(def.id);
      });
    return () => {
      cancelled = true;
    };
  }, [defQuery.data, slug, sessionId, setSessionId, hydrate]);

  const createSessionMutation = useMutation({
    mutationFn: () => createSession(slug),
    onSuccess: (sess) => {
      setSessionId(sess.sessionId);
      setResumeToken(sess.resumeToken);
      // Stocke le resumeToken (opaque hex) — JAMAIS le sessionId interne.
      if (defQuery.data) storeResumeToken(defQuery.data.id, sess.resumeToken);
      const def = defQuery.data;
      if (def?.identityMode === 'required') {
        setStage({ kind: 'identity_required' });
      } else {
        setStage({ kind: 'survey', blockIndex: 0 });
      }
    },
    onError: (err) => {
      setSubmitError(getErrorMessage(err, "Impossible de démarrer le questionnaire."));
    },
  });

  if (defQuery.isPending) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-bg">
        <p className="text-text-muted">Chargement…</p>
      </main>
    );
  }

  if (defQuery.isError || !defQuery.data) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-bg p-6">
        <div className="card max-w-prose">
          <h1 className="mb-3">Questionnaire indisponible</h1>
          <p className="text-text-muted">
            Ce questionnaire n'a pas pu être chargé. Il a peut-être été fermé,
            ou le lien est incorrect.
          </p>
        </div>
      </main>
    );
  }

  const def = defQuery.data;

  if (stage.kind === 'consent') {
    return (
      <ConsentScreen
        definition={def}
        onAccept={() => createSessionMutation.mutate()}
      />
    );
  }

  if (stage.kind === 'identity_required') {
    return (
      <IdentityScreen
        definition={def}
        mode="required"
        {...(identity !== null && { initial: identity })}
        onContinue={(newIdentity) => {
          setIdentity(newIdentity);
          setIdentityOptIn(true);
          // En required, on POST l'identité tout de suite (le serveur la
          // chiffre et la stocke séparément avant que les réponses arrivent).
          if (resumeToken) {
            void optInIdentity(slug, resumeToken, newIdentity);
          }
          setStage({ kind: 'survey', blockIndex: 0 });
        }}
      />
    );
  }

  if (stage.kind === 'identity_optional') {
    return (
      <IdentityScreen
        definition={def}
        mode="optional"
        {...(identity !== null && { initial: identity })}
        onContinue={(newIdentity, optIn) => {
          setIdentity(newIdentity);
          setIdentityOptIn(optIn);
          void doSubmit({
            slug,
            sessionId,
            resumeToken,
            identity: newIdentity,
            optIn,
            onStart: () => setStage({ kind: 'submitting' }),
            onSuccess: () => {
              clearResumeToken(def.id);
              setStage({ kind: 'done' });
              reset();
            },
            onError: (msg) => {
              setSubmitError(msg);
              setStage({ kind: 'identity_optional' });
            },
          });
        }}
      />
    );
  }

  if (stage.kind === 'submitting') {
    return (
      <main className="min-h-screen flex items-center justify-center bg-bg">
        <p className="text-text-muted">Envoi en cours…</p>
      </main>
    );
  }

  if (stage.kind === 'done') {
    return <ThankYouScreen />;
  }

  // stage.kind === 'survey'
  const idx = stage.blockIndex;
  const isLastBlock = idx >= def.blocks.length - 1;
  const block = def.blocks[idx];

  return (
    <main className="min-h-screen bg-bg pb-16">
      <Autosave slug={slug} />
      <SurveyHeader title={def.title} />
      <div className="max-w-3xl mx-auto px-4 pt-6 space-y-8">
        <ProgressBlock def={def} />
        <RespondentRenderer
          definition={def}
          blockIndex={idx}
          showErrors={false}
        />

        {submitError && (
          <div role="alert" className="notice max-w-prose">
            <p>{submitError}</p>
          </div>
        )}

        <NavigationButtons
          currentIndex={idx}
          isLastBlock={isLastBlock}
          onPrev={() => {
            if (idx > 0) setStage({ kind: 'survey', blockIndex: idx - 1 });
          }}
          onNext={() => {
            // Vérifie que les items required du bloc courant sont remplis
            const state = useRespondentStore.getState().engineState();
            if (!state) return;

            // Si fin par consentement déclenchée ailleurs (rare), montre helpline
            if (state.end) {
              setStage({ kind: 'done' });
              return;
            }

            const blockItemNames = (block?.items ?? [])
              .map((i) => i.name)
              .concat(
                (block?.sections ?? []).flatMap((s) => s.items.map((i) => i.name)),
              );
            const missing = state.missingRequired.filter((n) =>
              blockItemNames.includes(n),
            );
            const errs = state.errors.filter((e) => blockItemNames.includes(e.itemName));
            if (missing.length > 0 || errs.length > 0) {
              setSubmitError(
                'Quelques questions attendent encore une réponse — elles sont signalées ci-dessus.',
              );
              return;
            }
            setSubmitError(null);
            if (!isLastBlock) {
              setStage({ kind: 'survey', blockIndex: idx + 1 });
              window.scrollTo({ top: 0, behavior: 'auto' });
              return;
            }
            // Dernier bloc : route selon identityMode
            if (def.identityMode === 'optional') {
              setStage({ kind: 'identity_optional' });
              return;
            }
            // anonymous ou required (identité déjà collectée)
            void doSubmit({
              slug,
              sessionId,
              resumeToken,
              identity:
                def.identityMode === 'required' ? identity : null,
              optIn: def.identityMode === 'required',
              onStart: () => setStage({ kind: 'submitting' }),
              onSuccess: () => {
                clearResumeToken(def.id);
                setStage({ kind: 'done' });
                reset();
              },
              onError: (msg) => {
                setSubmitError(msg);
                setStage({ kind: 'survey', blockIndex: idx });
              },
            });
          }}
        />
      </div>
    </main>
  );
}

interface SurveyHeaderProps {
  title: string;
}
function SurveyHeader({ title }: SurveyHeaderProps) {
  return (
    <header className="bg-surface border-b border-surface-2 px-4 py-3 sticky top-0 z-10">
      <div className="max-w-3xl mx-auto flex items-center justify-between gap-4">
        <p className="text-sm font-medium text-text truncate" title={title}>
          {title}
        </p>
        <AutosaveStatusBadge />
      </div>
    </header>
  );
}

function ProgressBlock({ def }: { def: import('@webforge-survey/shared').SurveyDefinition }) {
  const answers = useRespondentStore((s) => s.answers);
  // useMemo plutôt qu'un selector ; le moteur retournant un nouvel objet à
  // chaque appel, on évite les boucles infinies de Zustand.
  const state = useMemo(() => evaluateSurvey(def, answers), [def, answers]);

  const visibleNonNote = state.visibleItems.filter((name) => {
    const it = findItemInDef(def, name);
    return it && it.type !== 'note';
  });
  const answered = visibleNonNote.filter((name) => {
    const v = answers[name];
    if (v === undefined || v === null) return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'string') return v.length > 0;
    if (typeof v === 'object') return Object.keys(v).length > 0;
    return true;
  });

  return <ProgressBar answered={answered.length} total={visibleNonNote.length} />;
}

function findItemInDef(
  def: import('@webforge-survey/shared').SurveyDefinition,
  name: string,
): import('@webforge-survey/shared').Item | undefined {
  for (const b of def.blocks) {
    for (const it of b.items ?? []) if (it.name === name) return it;
    for (const s of b.sections ?? [])
      for (const it of s.items) if (it.name === name) return it;
  }
  return undefined;
}

interface NavButtonsProps {
  currentIndex: number;
  isLastBlock: boolean;
  onPrev: () => void;
  onNext: () => void;
}
function NavigationButtons({
  currentIndex,
  isLastBlock,
  onPrev,
  onNext,
}: NavButtonsProps) {
  return (
    <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-surface-2">
      {currentIndex > 0 && (
        <button type="button" className="btn-secondary sm:flex-1" onClick={onPrev}>
          ← Précédent
        </button>
      )}
      <button type="button" className="btn-primary sm:flex-1" onClick={onNext}>
        {isLastBlock ? 'Terminer' : 'Continuer →'}
      </button>
    </div>
  );
}

function ThankYouScreen() {
  return (
    <main className="min-h-screen bg-bg py-12 px-4">
      <div className="space-y-6 max-w-prose mx-auto">
        <div className="card text-center">
          <h1 className="mb-3">Merci pour vos réponses.</h1>
          <p className="text-text-muted">
            Votre contribution est enregistrée. Vous pouvez fermer cet onglet
            quand vous le souhaitez.
          </p>
        </div>
        <HelplinePage variant="full" />
      </div>
    </main>
  );
}

interface DoSubmitArgs {
  slug: string;
  sessionId: string | null;
  resumeToken: string | null;
  identity: Identity | null;
  optIn: boolean;
  onStart: () => void;
  onSuccess: () => void;
  onError: (msg: string) => void;
}

async function doSubmit(args: DoSubmitArgs): Promise<void> {
  const { slug, sessionId, resumeToken, identity, optIn, onStart, onSuccess, onError } = args;
  if (!sessionId || !resumeToken) {
    onError("La session n'a pas pu être identifiée.");
    return;
  }
  const def = useRespondentStore.getState().definition;
  const answers = useRespondentStore.getState().answers;
  if (!def) {
    onError("La définition d'enquête n'est pas chargée.");
    return;
  }
  onStart();
  try {
    // En `optional` opt-in, on POST l'identité via le canal séparé /identity/opt-in
    // (anti-rebinding : la table d'identité ne référence pas la réponse). L'identité
    // ne doit alors PAS être incluse dans le payload de submit — le backend la
    // rejette en mode optional (elle ne transite que par opt-in).
    if (def.identityMode === 'optional' && optIn && identity) {
      await optInIdentity(slug, resumeToken, identity);
    }
    // Seul le mode `required` véhicule l'identité dans le submit ; optional la
    // passe par opt-in, anonymous n'en a jamais.
    const submitIdentity = def.identityMode === 'required' ? identity : null;
    const payload = buildPayload(def, answers, submitIdentity);
    await submitSession(slug, sessionId, resumeToken, payload);
    onSuccess();
  } catch (err) {
    onError(getErrorMessage(err, "L'envoi a échoué. Veuillez réessayer."));
  }
}
